import { Component, type ErrorInfo, type ReactNode } from "react";
// Store-free recovery seam (G33) — safe to import from this last-line
// boundary even if the store module is what threw.
import { reload as reloadConn, switchToMock as switchToMockConn } from "@/lib/connectionRecovery";

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Mirror of the store's detectMode() — duplicated here (URL + iframe
 * check only) so the last-line boundary stays renderable even if the
 * store module itself is what threw. Live/embedded sessions get the
 * mock-switch demoted behind a "Developer options" disclosure.
 */
function isLiveSession(): boolean {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get("live") === "1") return true;
    if (u.searchParams.get("mock") === "1") return false;
    return window.parent !== window;
  } catch {
    return false;
  }
}

/**
 * Last-line defence: when *any* descendant throws during render or in a
 * lifecycle, catch it here instead of letting React unmount the entire
 * tree to a blank `<div id="root"></div>` (which is what produced the
 * "iframe goes black after a second" bug).
 *
 * On error we render a contained recovery panel inside the existing
 * chrome — the operator can copy the stack to clipboard, reload, or
 * (in embedded mode) ask the host widget to re-deliver auth.token.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Mirror to console so the iframe's parent DevTools sees the trace too.
    console.error("[operator-spa] ErrorBoundary caught:", error, info?.componentStack);
    this.setState({ info });
  }

  private reload = reloadConn;

  private copyTrace = async () => {
    const blob = `${this.state.error?.message ?? ""}\n\n${this.state.error?.stack ?? ""}\n\nComponent stack:${this.state.info?.componentStack ?? ""}`;
    try {
      await navigator.clipboard.writeText(blob);
    } catch {
      // ignore — fallback would need a hidden textarea + select+copy
    }
  };

  private switchToMock = switchToMockConn;

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error.message || "Unknown error";
    const live = isLiveSession();
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text">
        <div className="m-4 max-w-xl rounded-xl border border-critical/30 bg-surface p-6 shadow-xl">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-critical/15 text-critical text-xl">!</span>
            <div>
              <h2 className="text-lg font-semibold">operator-spa hit a render error</h2>
              <p className="mt-1 text-xs text-text-muted">
                The component tree was unmounted to avoid a partially-broken UI.
                You can recover with one of the buttons below.
              </p>
            </div>
          </div>
          <pre className="mt-4 max-h-64 overflow-auto rounded-md border border-edge bg-surface-2 p-3 text-[11px] leading-relaxed text-text-muted">
{msg}
{this.state.error.stack ?? ""}
          </pre>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={this.reload}
              className="flex h-9 items-center gap-1.5 rounded-md border border-accent/40 bg-accent/15 px-3 text-xs font-semibold text-accent hover:bg-accent/25"
            >
              Reload
            </button>
            <button
              onClick={this.copyTrace}
              className="flex h-9 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent/40"
            >
              Copy stack to clipboard
            </button>
            {!live && (
              <button
                onClick={this.switchToMock}
                className="flex h-9 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent/40"
              >
                Switch to mock mode
              </button>
            )}
          </div>
          {/* Live/embedded: the mock-switch is a developer escape hatch, not a
              recovery peer — an operator clicking it lands on a SIMULATOR of
              their plant. Keep it reachable but collapsed and explicit. */}
          {live && (
            <details className="mt-3">
              <summary className="cursor-pointer text-[11px] text-text-faint hover:text-text-muted">
                Developer options
              </summary>
              <button
                onClick={this.switchToMock}
                className="mt-2 flex h-9 items-center gap-1.5 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent/40"
              >
                Show SIMULATED data — not your site
              </button>
            </details>
          )}
          <div className="mt-4 text-[10px] text-text-faint">
            If you're seeing this inside an EMS dashboard iframe and live data was expected:
            verify the host delivered authentication + site context within ~4 s. Ask
            the EMS administrator to check the dashboard widget configuration.
          </div>
        </div>
      </div>
    );
  }
}
